import { CacheService } from '../../../services/cache.service';
import { MapNavigationService } from '../../core/map-navigation.service';
import { MapFeatureColors } from '../../core/map-object-colors';
import { DensityCellData, DensitySource, densityParams } from '../../core/density';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject, Subscription, map } from 'rxjs';
import L from 'leaflet';

import {
  PlaceFeatureBase,
  PlaceMapObject,
  MapSelectionObject,
} from '../../core/base/place-feature.base';
import { MapObjectPopoverSection } from '../../core/map-object-popover';
import { MapObjectDetailsService } from '../../../services/map-object-details.service';
import { PlaceMarkerLayoutService } from '../../core/place-marker-layout.service';
import {
  MapTileCache,
  MapTileResponse,
  MapTotalResponse,
  MapViewport,
  MAP_CLUSTER_MODE_SWITCH_ZOOM,
} from '../../core/map-tile-cache';
import { mapSelection } from '../../core/spatial/map-viewport';
import type { MapTile } from '../../core/spatial/map-viewport';

export interface CustomerMapObject extends PlaceMapObject {
  readonly id: number;
  readonly displayName: string;
  readonly customerTypeId: number | null;
  readonly customerTypeName: string | null;
  readonly assigneeUserId: number | null;
  readonly assigneeName: string | null;
  readonly isActive: boolean;
}

export interface CustomerMapFilter {
  readonly search?: string;
  readonly customerTypeId?: number;
  readonly assigneeUserId?: number;
  readonly includeInactive?: boolean;
}

@Injectable({ providedIn: 'root' })
export class CustomerFeature extends PlaceFeatureBase<CustomerMapObject> implements DensitySource {
  override readonly page = '/customers';
  readonly densityChanged$ = new Subject<void>();

  loadDensity(grid: MapViewport): Observable<readonly DensityCellData[]> {
    return this.http.get<readonly DensityCellData[]>(
      '/api/customers/map/density',
      { params: densityParams(customerFilterParams(this.activeFilter), grid) },
    );
  }

  static readonly Key = 'customers';

  private readonly tileCache = new MapTileCache<CustomerMapObject>(
    this.cache, 'map:customers', point => point.id,
  );
  private viewport?: MapViewport;
  private activeFilter: CustomerMapFilter = {};
  get contextState(): Readonly<Record<string, unknown>> { return { filters: { ...this.activeFilter } }; }
  private request?: Subscription;
  private totalRequest?: Subscription;
  private initialized = false;
  private refreshWhenEnabled = false;

  constructor(
    private readonly http: HttpClient,
    details: MapObjectDetailsService,
    markerLayout: PlaceMarkerLayoutService,
    cache: CacheService,
    navigation: MapNavigationService,
  ) {
    super({
      key: CustomerFeature.Key,
      name: 'Customers',
      color: MapFeatureColors.customers,
    }, details, markerLayout, cache, navigation);
  }

  override resetSession(): void {
    this.request?.unsubscribe();
    this.totalRequest?.unsubscribe();
    this.viewport = undefined;
    this.activeFilter = {};
    this.initialized = false;
    this.refreshWhenEnabled = false;
    super.resetSession();
  }

  async init(): Promise<void> {
    this.initialized = true;
    this.refreshTotal();
    this.refresh();
  }

  filter(filter: CustomerMapFilter): void {
    this.activeFilter = { ...filter };
    this.densityChanged$.next();
    if (!this.initialized) return;
    this.refreshTotal();
    this.refresh();
  }

  override onViewportChanged(viewport: MapViewport): void {
    this.viewport = viewport;
    if (this.initialized) this.refresh();
  }

  refresh(): void {
    if (!this.viewport) return;
    if (!this.enabled) {
      this.refreshWhenEnabled = true;
      return;
    }
    this.refreshWhenEnabled = false;
    this.request?.unsubscribe();
    this.setLoading(true);
    const viewport = this.viewport;
    const filter = this.activeFilter;
    const clusterMode = viewport.zoom < MAP_CLUSTER_MODE_SWITCH_ZOOM;
    const request = this.tileCache.filter(
      viewport,
      customerFilterKey(filter),
      requestViewport => this.load(filter, requestViewport),
    );
    this.request = request.subscribe({
      next: response => {
        if (clusterMode) {
          this.replaceClusteredObjects(
            response.points,
            response.clusters ?? [],
          );
        } else {
          this.replaceObjects(response.points);
        }
      },
      error: () => this.setLoading(false),
      complete: () => this.setLoading(false),
    });
  }

  clearCacheAndRefresh(): void {
    this.densityChanged$.next();
    this.tileCache.clear();
    this.refreshTotal();
    this.refresh();
  }

  override render(): void {
    if (this.enabled && this.refreshWhenEnabled) {
      this.refresh();
      return;
    }
    super.render();
  }

  protected objectPopover(
    object: CustomerMapObject,
  ): MapObjectPopoverSection {
    return {
      type: 'Customer',
      title: object.displayName,
      values: [
        { label: 'Type', value: object.customerTypeName },
        { label: 'Assignee', value: object.assigneeName },
        { label: 'Active', value: object.isActive },
      ],
    };
  }

  protected objectId(object: CustomerMapObject): number {
    return object.id;
  }

  protected selectObjects(
    bounds: L.LatLngBounds,
    polygon?: readonly L.LatLng[],
    clusterTiles?: readonly MapTile[],
  ): Observable<readonly MapSelectionObject[]> {
    const params = customerFilterParams(this.activeFilter);
    return this.http.post<{ objects: readonly MapSelectionObject[] }>(
      '/api/customers/map/selection',
      mapSelection(bounds, polygon, clusterTiles),
      { params },
    ).pipe(map(response => response.objects));
  }

  private load(
    filter: CustomerMapFilter,
    viewport: MapViewport,
  ): Observable<MapTileResponse<CustomerMapObject>> {
    const params = customerFilterParams(filter)
      .set('south', viewport.south)
      .set('west', viewport.west)
      .set('north', viewport.north)
      .set('east', viewport.east)
      .set('zoom', Math.floor(viewport.zoom));

    return this.http.get<MapTileResponse<CustomerMapObject>>(
      '/api/customers/map',
      { params },
    );
  }

  private refreshTotal(): void {
    this.totalRequest?.unsubscribe();
    this.totalRequest = this.http.get<MapTotalResponse>(
      '/api/customers/total',
      { params: customerFilterParams(this.activeFilter) },
    ).subscribe({
      next: response => this.setTotal(response.total),
      error: () => undefined,
    });
  }
}

function customerFilterParams(filter: CustomerMapFilter): HttpParams {
  let params = new HttpParams();
  if (filter.search?.trim()) params = params.set('search', filter.search.trim());
  if (filter.customerTypeId != null) {
    params = params.set('customerTypeId', filter.customerTypeId);
  }
  if (filter.assigneeUserId != null) {
    params = params.set('assigneeUserId', filter.assigneeUserId);
  }
  if (filter.includeInactive) params = params.set('includeInactive', true);
  return params;
}

function customerFilterKey(filter: CustomerMapFilter): string {
  return JSON.stringify({
    search: filter.search?.trim() ?? '',
    customerTypeId: filter.customerTypeId ?? null,
    assigneeUserId: filter.assigneeUserId ?? null,
    includeInactive: filter.includeInactive ?? false,
  });
}
